import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/* ------------------------------------------------------------------ layout */

export function Card({
  children,
  flush,
  className = '',
}: {
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return <div className={`card ${flush ? 'card--flush' : ''} ${className}`.trim()}>{children}</div>;
}

export function CardHead({ title, sub, action }: { title: string; sub?: string; action?: ReactNode }) {
  return (
    <div className="card__head">
      <div>
        <div className="card__title">{title}</div>
        {sub && <div className="card__sub">{sub}</div>}
      </div>
      {action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
    </div>
  );
}

/* ----------------------------------------------------------------- buttons */

type ButtonVariant = 'primary' | 'ghost' | 'danger' | 'success' | 'link';

export function Button({
  children,
  variant = 'ghost',
  size,
  loading,
  disabled,
  onClick,
  type = 'button',
  title,
}: {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: 'sm';
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  title?: string;
}) {
  return (
    <button
      type={type}
      title={title}
      className={`btn btn--${variant} ${size === 'sm' ? 'btn--sm' : ''}`.trim()}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading && <span className={`spinner ${variant === 'ghost' ? 'spinner--dark' : ''}`} />}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ badges */

export type BadgeTone = 'neutral' | 'blue' | 'green' | 'amber' | 'red' | 'purple' | 'orange';

export function Badge({ children, tone = 'neutral', dot }: { children: ReactNode; tone?: BadgeTone; dot?: boolean }) {
  return (
    <span className={`badge badge--${tone}`}>
      {dot && <span className="badge__dot" />}
      {children}
    </span>
  );
}

/**
 * One place that decides what colour a status is, so the same word never
 * appears green on one screen and amber on another.
 */
const TONES: Record<string, BadgeTone> = {
  // lifecycle
  LIVE: 'green', DRAFT: 'neutral', PENDING_PAYMENT: 'amber', HOLD: 'orange',
  EXPIRED: 'neutral', DELETED: 'neutral', REJECTED: 'red', PENDING_REVIEW: 'amber', SUSPENDED: 'orange',
  // kyc / verification
  VERIFIED: 'green', PENDING: 'amber', NOT_STARTED: 'neutral',
  SUBMITTED: 'amber', NOT_SUBMITTED: 'neutral',
  // payments
  CAPTURED: 'green', CREATED: 'neutral', AUTHORIZED: 'blue', FAILED: 'red', REFUNDED: 'purple',
  // payouts — BLOCKED is amber, not red: it is a deliberate hold, not a failure
  PAID: 'green', BLOCKED: 'amber', PROCESSING: 'blue', REVERSED: 'purple',
  // bookings
  REQUESTED: 'amber', ACCEPTED: 'blue', IN_PROGRESS: 'blue', CLOSED: 'green', CANCELLED: 'neutral',
  // moderation
  OPEN: 'red', ACTIONED: 'green', DISMISSED: 'neutral', APPROVED: 'green', MERGED: 'purple',
  // roles
  ADMIN: 'purple', AGENT: 'orange', SELLER: 'blue', SERVICE_PROVIDER: 'blue', CUSTOMER: 'neutral',
  // campaigns
  SENT: 'green', SCHEDULED: 'blue', SENDING: 'amber',
};

export function StatusBadge({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="cell-muted">—</span>;
  const label = value.replace(/_/g, ' ').toLowerCase();
  return <Badge tone={TONES[value] ?? 'neutral'}>{label.charAt(0).toUpperCase() + label.slice(1)}</Badge>;
}

/* ------------------------------------------------------------------ inputs */

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      {label && <label className="field__label">{label}</label>}
      {children}
      {error ? <div className="field__error">{error}</div> : hint ? <div className="field__hint">{hint}</div> : null}
    </div>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`input ${props.className ?? ''}`.trim()} />;
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`textarea ${props.className ?? ''}`.trim()} />;
}

export function Select({
  options,
  placeholder,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & {
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}) {
  return (
    <select {...props} className={`select ${props.className ?? ''}`.trim()}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** Debounced so typing does not fire a request per keystroke. */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (local !== value) onChange(local);
    }, 320);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  return (
    <div className="search">
      <span className="search__icon">⌕</span>
      <Input value={local} placeholder={placeholder} onChange={(e) => setLocal(e.target.value)} />
    </div>
  );
}

export function Chips<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: string; count?: number }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="chips">
      {options.map((o) => (
        <button
          key={o.value}
          className={`chip ${o.value === value ? 'chip--active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count !== undefined && <span className="chip__count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ states */

export function Empty({ icon = '🗂', title, body }: { icon?: string; title: string; body?: string }) {
  return (
    <div className="empty">
      <div className="empty__icon">{icon}</div>
      <div className="empty__title">{title}</div>
      {body && <div className="empty__body">{body}</div>}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <tbody>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              <div className="skeleton" style={{ height: 13, width: c === 0 ? '62%' : '44%' }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

export function Notice({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'danger';
  title?: string;
  children: ReactNode;
}) {
  const icon = tone === 'danger' ? '⛔' : tone === 'warn' ? '⚠️' : 'ℹ️';
  return (
    <div className={`notice notice--${tone}`}>
      <span className="notice__icon">{icon}</span>
      <div>
        {title && <div className="notice__title">{title}</div>}
        <div>{children}</div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- pagination */

export function Pagination({
  page,
  totalPages,
  total,
  limit,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPage: (p: number) => void;
}) {
  if (total === 0) return null;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <div className="pagination">
      <span className="pagination__info">
        {from}–{to} of {new Intl.NumberFormat('en-IN').format(total)}
      </span>
      <div className="spacer" />
      <Button size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ← Prev
      </Button>
      <span className="pagination__info">
        Page {page} of {totalPages}
      </span>
      <Button size="sm" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
        Next →
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ modals */

export function Modal({
  title,
  sub,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  sub?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    // Stop the page behind scrolling while a dialog is open.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'modal--wide' : ''}`.trim()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal__head">
          <div className="modal__title">{title}</div>
          {sub && <div className="modal__sub">{sub}</div>}
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__foot">{footer}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ toasts */

type Toast = { id: number; message: string; tone: 'info' | 'success' | 'error' };
const ToastContext = createContext<(message: string, tone?: Toast['tone']) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, tone: Toast['tone'] = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone !== 'info' ? `toast--${t.tone}` : ''}`.trim()}>
            <span>{t.tone === 'error' ? '⚠' : t.tone === 'success' ? '✓' : 'ℹ'}</span>
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

/* ------------------------------------------------------------------ avatar */

export function Avatar({ name, src, size }: { name?: string | null; src?: string | null; size?: 'lg' }) {
  const letters = useMemo(() => {
    if (!name?.trim()) return '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '')).toUpperCase();
  }, [name]);
  return (
    <div className={`avatar ${size === 'lg' ? 'avatar--lg' : ''}`.trim()}>
      {src ? <img src={src} alt="" /> : letters}
    </div>
  );
}
